/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import * as appStrings from '@touchplan/strings';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { Organization } from 'src/app/app-data/models/Organization';
import { GenericDialogComponent } from 'src/app/common/generic-dialog/generic-dialog.component';
import { OrganizationService } from '../../services/organization.service';
import { MatDialog } from '@angular/material/dialog';

@Component({
	selector: 'add-edit-organization-app',
	templateUrl: './add-edit-organization-app.component.html',
	styleUrls: ['./add-edit-organization-app.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddEditOrganizationAppComponent implements OnInit, OnDestroy {
	@Output() public memberCommitEvent = new EventEmitter<Organization>();
	@Output() public itemFormEvent = new EventEmitter<Organization>();
	public orgApplicationFormGroup: UntypedFormGroup = this.formBuilder.group({});
	public canDelete$: Observable<boolean>;
	public canSave$: Observable<boolean>;
	public appStrings = appStrings;

	private shutdownSubject$: Subject<void> = new Subject();

	constructor(
		public organizationService: OrganizationService,
		private formBuilder: UntypedFormBuilder,
		protected dialog: MatDialog
	) {}

	public ngOnInit(): void {
		this.canDelete$ = this.organizationService.isEditing$;

		this.canSave$ = this.orgApplicationFormGroup.statusChanges.pipe(
			map((change) => {
				const isDirty = this.orgApplicationFormGroup.dirty;
				const isValid = change === 'VALID';
				return isDirty && isValid;
			})
		);
	}

	public handleItemEvent(type: string): void {
		switch (type) {
			case 'Cancel':
				this.organizationService.cancel();
				break;
			case 'Save':
				this.organizationService.save();
				break;
			case 'Delete':
				this.dialog
					.open(GenericDialogComponent, {
						data: {
							title: appStrings.DELETE_DIALOG_TEXT,
							yesButtonText: appStrings.DELETE_DIALOG_YES,
							noButtonText: appStrings.DELETE_DIALOG_NO,
						},
						disableClose: true,
					})
					.afterClosed()
					.pipe(takeUntil(this.shutdownSubject$))
					.subscribe((actionDelete) => {
						if (actionDelete) {
							this.organizationService.deleteApplication();
						}
					});
				break;
		}
	}

	public ngOnDestroy(): void {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}
}
